import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import UserForm from './components/UserForm';
import UserStatsPanel from './components/UserStatsPanel';
import BulkActionsModal from './components/BulkActionsModal';
import { userManagementService } from '../../services/supabaseService';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userManagementService?.getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      const newUser = await userManagementService?.createUser(userData);
      if (newUser) {
        await fetchUsers();
        setIsAddingUser(false);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      const updated = await userManagementService?.updateUser(userId, userData);
      if (updated) {
        await fetchUsers();
        setEditingUser(null);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const success = await userManagementService?.deleteUser(userId);
      if (success) {
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setToastMsg({ message: 'Failed to delete user.', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await userManagementService?.updateUserStatus(userId, newStatus);
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = !searchQuery || 
      user?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.status === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleSelectAll = () => {
    if (selectedUsers?.length === filteredUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers?.map(u => u?.id));
    }
  };

  const handleBulkAction = async (action, data) => {
    try {
      await userManagementService?.bulkUpdateUsers(selectedUsers, action, data);
      await fetchUsers();
      setSelectedUsers([]);
      setShowBulkActions(false);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  };

  const userStats = {
    total: users?.length || 0,
    active: users?.filter(u => u?.status === 'active')?.length || 0,
    admins: users?.filter(u => u?.role === 'admin')?.length || 0,
    newThisMonth: users?.filter(u => {
      const createdDate = new Date(u?.createdAt);
      const now = new Date();
      return createdDate?.getMonth() === now?.getMonth() && 
             createdDate?.getFullYear() === now?.getFullYear();
    })?.length || 0
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    };
    return styles?.[status] || styles?.active;
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      user: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    };
    return styles?.[role] || styles?.user;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  User Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Manage user accounts, roles, and permissions
                </p>
              </div>
              <Button
                onClick={() => navigate('/admin-dashboard')}
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                className="hidden md:flex"
              >
                Back to Dashboard
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-2xl font-bold text-foreground">{userStats?.total}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active</p>
                    <p className="text-2xl font-bold text-foreground">{userStats?.active}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Admins</p>
                    <p className="text-2xl font-bold text-foreground">{userStats?.admins}</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={20} className="text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">New This Month</p>
                    <p className="text-2xl font-bold text-foreground">{userStats?.newThisMonth}</p>
                  </div>
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} className="text-orange-600 dark:text-orange-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Actions */}
          <div className="bg-card rounded-lg shadow-elevation-sm border border-border mb-6">
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    leftIcon="Search"
                  />
                </div>

                {/* Role Filter */}
                <div className="w-full md:w-48">
                  <Select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e?.target?.value)}
                  >
                    <option value="all">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="w-full md:w-48">
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e?.target?.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </div>

                {/* Add User Button */}
                <Button
                  onClick={() => setIsAddingUser(true)}
                  iconName="UserPlus"
                  iconPosition="left"
                  className="w-full md:w-auto"
                >
                  Add User
                </Button>
              </div>

              {/* Bulk Actions */}
              {selectedUsers?.length > 0 && (
                <div className="mt-4 flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <span className="text-sm text-blue-900 dark:text-blue-300">
                    {selectedUsers?.length} user(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowBulkActions(true)}
                      iconName="Settings"
                      iconPosition="left"
                    >
                      Bulk Actions
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUsers([])}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-card rounded-lg shadow-elevation-sm border border-border overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading users...</p>
              </div>
            ) : filteredUsers?.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedUsers?.length === filteredUsers?.length}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">User</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Registered</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Last Active</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredUsers?.map((user) => (
                      <tr key={user?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers?.includes(user?.id)}
                            onChange={() => handleSelectUser(user?.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold">
                              {user?.fullName?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{user?.fullName}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">{user?.email}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(user?.role)}`}>
                            {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(user?.status || 'active')}`}>
                            {(user?.status || 'active')?.charAt(0)?.toUpperCase() + (user?.status || 'active')?.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">{formatDate(user?.createdAt)}</td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">{formatDate(user?.updatedAt)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowStats(true);
                              }}
                              title="View Stats"
                            >
                              <Icon name="BarChart" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingUser(user)}
                              title="Edit User"
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(user?.id, user?.status || 'active')}
                              title={user?.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              <Icon name={user?.status === 'active' ? 'XCircle' : 'CheckCircle'} size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user?.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              title="Delete User"
                            >
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add/Edit User Modal */}
      {(isAddingUser || editingUser) && (
        <UserForm
          user={editingUser}
          onSave={editingUser ? (data) => handleUpdateUser(editingUser?.id, data) : handleAddUser}
          onClose={() => {
            setIsAddingUser(false);
            setEditingUser(null);
          }}
        />
      )}

      {/* User Stats Panel */}
      {showStats && selectedUser && (
        <UserStatsPanel
          user={selectedUser}
          onClose={() => {
            setShowStats(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <BulkActionsModal
          selectedCount={selectedUsers?.length}
          onAction={handleBulkAction}
          onClose={() => setShowBulkActions(false)}
        />
      )}
      {toastMsg && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toastMsg?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-success text-success-foreground'}`}>
          {toastMsg?.message}
        </div>
      )}
    </div>
  );
};

export default UserManagement;